import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import WarningIcon from '@mui/icons-material/Warning'
import { alpha } from '@mui/material'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Alert from '@mui/material/Alert'
import Backdrop from '@mui/material/Backdrop'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Snackbar from '@mui/material/Snackbar'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import KNAdvancedDropdown from 'components/KNAdvancedDropdown/KNAdvancedDropdown'
import KNCaption from 'components/KNCaption/KNCaption'
import { KNCopyIcon } from 'components/KNIcon/KNIcon'
import { KNGenericMapIcon } from 'components/KNIcon/KNMapIcon'
import { KNShareIcon } from 'components/KNIcon/KNMaterialIcon'
import { getPolygonCenter } from 'components/KNMapLibre/helpers'
import { TripDetailsContext } from 'context/trips/TripDetailsContext'
import { analyticsEvent } from 'global/helpers/analytics'
import useSuppressFlag from 'global/hooks/useSuppressFlag'
import { createRef, type ReactElement, type SyntheticEvent, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TripData } from 'screens/TripDashboard/TripDashboard.types'
import { useDebounce } from 'use-debounce'
import ParkingLocationShareDialog from './ParkingLocationShareDialog'
import { formatParkingLocationDescription, getParkingLocationColor } from './TripDetails.helpers'
import { type ParkingLocation, ParkingLocationType } from './TripDetails.types'

export interface TripParkingLocationsProps {
  readonly trip: TripData
  readonly parkingLocations: ParkingLocation[]
  readonly filteredParkingLocations: ParkingLocation[]
  readonly stopsViewAvailable?: boolean
}

const TripParkingLocations = ({
  trip,
  parkingLocations,
  filteredParkingLocations,
  stopsViewAvailable,
}: TripParkingLocationsProps): ReactElement | null => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { suppressIndefinitely, isSuppressed } = useSuppressFlag('parking-locations-disclaimer')
  const [disclaimerVisible, setDisclaimerVisible] = useState(!isSuppressed)
  const [tripDetailsState, tripDetailsDispatch] = useContext(TripDetailsContext)
  const [payload, setPayload] = useState<ParkingLocationType[] | null>(null)
  const [debouncedPayload] = useDebounce(payload, 100)
  const [parkingCopied, setParkingCopied] = useState<boolean>()
  const [shareDialogOpen, setShareDialogOpen] = useState(false)
  const [activeParkingLocation, setActiveParkingLocation] = useState<ParkingLocation | null>(null)
  const refs = useRef({})

  filteredParkingLocations.forEach(parkingLocation => {
    refs.current[parkingLocation.entityId] = createRef()
  })

  useEffect(() => {
    if (debouncedPayload) {
      tripDetailsDispatch({
        type: 'setParkingLocationTypes',
        payload: debouncedPayload,
      })
    }
    setPayload(null)
  }, [debouncedPayload])

  const handleOnChange = (parkingLocation: ParkingLocation) => (_event: SyntheticEvent, isExpanded: boolean) => {
    setActiveParkingLocation(isExpanded ? parkingLocation : null)

    tripDetailsDispatch({
      type: 'setParkingLocationFocus',
      payload: parkingLocation,
    })

    tripDetailsDispatch({
      type: 'setDetailsView',
      payload: stopsViewAvailable ? 'sequence_map' : 'locations_map',
    })
    const parkingLocationGeoPoint = parkingLocation.geometry.type === 'POLYGON'
      ? getPolygonCenter(parkingLocation.geometry.coordinates)
      : parkingLocation.geometry.coordinates[0]
    const isAlreadyFocused = tripDetailsState.focusGeoPoint?.latitude === parkingLocationGeoPoint?.latitude
      && tripDetailsState.focusGeoPoint?.longitude === parkingLocationGeoPoint?.longitude
    tripDetailsDispatch({
      type: 'setFocusGeoPoint',
      payload: isAlreadyFocused ? undefined : parkingLocationGeoPoint,
    })
  }
  const getParkingLocationString = (parkingLocation: ParkingLocation): string => {
    return [parkingLocation.properties.title, parkingLocation.properties.address, parkingLocation.properties.group_name].join(', ')
  }

  const handleCopy = async (parkingLocation: ParkingLocation): Promise<void> => {
    await navigator.clipboard.writeText(getParkingLocationString(parkingLocation))
    setParkingCopied(true)
  }

  const handleClose = (): void => {
    setParkingCopied(false)
  }

  const getParkingLocationCount = (parkingLocationType: ParkingLocationType): number => {
    return parkingLocations.filter(parkingLocation => parkingLocation.type === parkingLocationType).length
  }

  useEffect(() => {
    setActiveParkingLocation(tripDetailsState.parkingLocationFocus ?? null)
    if (tripDetailsState.parkingLocationFocus) {
      const targetRef = refs.current[tripDetailsState.parkingLocationFocus.entityId]
      if (targetRef?.current) {
        targetRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
        handleOnChange(tripDetailsState.parkingLocationFocus)
      }
    }
  }, [tripDetailsState.parkingLocationFocus])

  const getParkingLocationBackgroundColour = (id: string): string => {
    if (tripDetailsState.parkingLocationFocus?.entityId === id) {
      return theme.palette.lightblue[5]
    } else {
      return theme.palette.success.contrastText
    }
  }

  const handleShareClick = useCallback((): void => {
    setShareDialogOpen(true)
    analyticsEvent('polestar_cs_share_parking_location_button_clicked')
  }, [])

  const handleShareAction = useCallback((): void => {
    setShareDialogOpen(false)
  }, [])

  const handleShareClose = useCallback((): void => {
    setShareDialogOpen(false)
  }, [])

  return (
    <Stack sx={{ minHeight: 0, position: 'relative' }}>
      <Box p={1}>
        <KNAdvancedDropdown
          options={Object.keys(ParkingLocationType).map((type) => ({
            value: type,
            label: t(`screens.cs.trip_details.parking_locations.${type}`),
            icon: <KNGenericMapIcon size={24} color={getParkingLocationColor(ParkingLocationType[type])} label="P" />,
            count: getParkingLocationCount(ParkingLocationType[type]),
          }))}
          value={tripDetailsState.parkingLocationTypes}
          onChange={(newValue): void => {
            setPayload(newValue as ParkingLocationType[])
          }}
          onDelete={(value) => {
            setPayload(tripDetailsState.parkingLocationTypes?.filter((type) => type.toString() !== value.toString()))
          }}
          size="small"
          multiple
        />
      </Box>
      {filteredParkingLocations.length === 0 ? (
        <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
          <Typography variant="p3" color="primary.light" py={2}>
            {t('screens.cs.trip_details.sidebar.no_parking_locations')}
          </Typography>
        </Stack>
      ) : (
        <Stack sx={{ overflowY: 'auto' }}>
          {filteredParkingLocations.map((parkingLocation) => (
            <Accordion
              key={parkingLocation.entityId}
              expanded={activeParkingLocation?.entityId === parkingLocation.entityId}
              onChange={handleOnChange(parkingLocation)}
              ref={refs.current[parkingLocation.entityId]}
              sx={{
                backgroundColor: getParkingLocationBackgroundColour(parkingLocation.entityId),
                '&:hover': {
                  backgroundColor: theme.palette.lightblue[5],
                },
              }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={1} sx={{ marginLeft: -1 }}>
                  <Box sx={{ flexShrink: 0 }}>
                    <KNGenericMapIcon size={32} color={getParkingLocationColor(parkingLocation.type)} label="P" />
                  </Box>
                  <Stack>
                    <Stack direction="row" spacing={1}>
                      <Typography variant="p4" color="primary.main">
                        {parkingLocation.properties.title}
                      </Typography>
                      <KNCopyIcon
                        fontSize="small"
                        color="primary"
                        onClick={(event) => {
                          event.stopPropagation()
                          // eslint-disable-next-line @typescript-eslint/no-floating-promises
                          handleCopy(parkingLocation)
                        }}
                      />
                    </Stack>
                    <Typography color="primary.main" variant="p3" component="p">
                      {parkingLocation.properties.address}
                    </Typography>
                    {parkingLocation.type === ParkingLocationType.KUEHNE_NAGEL ? (
                      <KNCaption
                        iconColor={getParkingLocationColor(parkingLocation.type)}
                        icon={<WarningIcon />}
                      >
                        {t('screens.cs.trip_details.sidebar.parking_reservation')}
                      </KNCaption>
                    ) : (
                      <Typography color={getParkingLocationColor(parkingLocation.type)} variant="p5">
                        {t(`screens.cs.trip_details.parking_locations.${parkingLocation.type}`)}
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ paddingLeft: '3rem' }}>
                {parkingLocation.properties.long_description && (
                  <Typography color="text.main" variant="p5" component="p" sx={{ whiteSpace: 'pre-wrap', textWrap: 'pretty' }}>
                    {formatParkingLocationDescription(parkingLocation.properties.long_description)}
                  </Typography>
                )}
                <Stack alignItems="end" sx={{ marginTop: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    endIcon={<KNShareIcon />}
                    onClick={handleShareClick}
                  >
                    {t('screens.cs.trip_details.sidebar.share_parking_location_short')}
                  </Button>
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      )}
      <Button
        variant="text"
        size="small"
        sx={{
          alignSelf: 'start',
        }}
        onClick={() => {
          setDisclaimerVisible(true)
        }}
      >
        {t('screens.cs.trip_details.sidebar.parking_locations_disclaimer.header')}
      </Button>
      {disclaimerVisible && (
        <>
          <Backdrop
            open
            transitionDuration={0}
            sx={{ position: 'absolute' }}
          />
          <Paper
            elevation={0}
            sx={{
              position: 'absolute',
              bottom: '0.5rem',
              left: '0.5rem',
              right: '0.5rem',
              padding: 2,
              backgroundColor: alpha(theme.palette.primary.dark, 0.85),
              backdropFilter: 'blur(3px)',
              color: theme.palette.success.contrastText,
            }}
          >
            <Stack direction="column" spacing={2}>
              <Typography component="p" variant="p6">
                {t('screens.cs.trip_details.sidebar.parking_locations_disclaimer.header')}
              </Typography>
              <Typography component="p" variant="p5">
                {t('screens.cs.trip_details.sidebar.parking_locations_disclaimer.content')}
              </Typography>
              <Typography component="p" variant="p5">
                {t('screens.cs.trip_details.sidebar.parking_locations_disclaimer.footer')}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                color="inherit"
                sx={{
                  color: 'white !important',
                  alignSelf: 'start',
                }}
                onClick={(): void => {
                  setDisclaimerVisible(false)
                  suppressIndefinitely()
                }}
              >
                {t('screens.cs.trip_details.sidebar.parking_locations_disclaimer.confirmation')}
              </Button>
            </Stack>
          </Paper>
        </>
      )}

      {activeParkingLocation && (
        <ParkingLocationShareDialog
          payload={{
            trip: trip,
            parkingLocation: activeParkingLocation,
            // weblinkToken,
          }}
          open={shareDialogOpen}
          onAction={handleShareAction}
          onClose={handleShareClose}
        />
      )}

      <Snackbar
        open={parkingCopied}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          icon={<CheckCircleIcon color="success" />}
        >
          <Typography variant="p4" component="p" color="primary">{t('screens.cs.trip_details.sidebar.parking_location_copied')}</Typography>
          <Typography variant="p3" component="p" color="primary">{activeParkingLocation?.properties.title}</Typography>
        </Alert>
      </Snackbar>
    </Stack>
  )
}

export default TripParkingLocations
